import { FreeSpinSubType } from '@atlas/bonuses';

import { DEPOSIT_LIKE_TYPES, UI_GROUP } from './bonusFeedUtils';

export const pad2 = n => String(Math.max(0, n)).padStart(2, '0');

/**
 * Normalizes API timestamps to epoch ms
 */
export const normalizeEpochMs = raw => {
  if (raw == null || raw === '') {
    return null;
  }
  const n = typeof raw === 'number' ? raw : new Date(raw).getTime();
  if (!Number.isFinite(n) || n <= 0) {
    return null;
  }
  return n < 1e12 ? n * 1000 : n;
};

export const formatFinishedDateDDMMYYYY = epochMs => {
  const t = normalizeEpochMs(epochMs);
  if (t == null) {
    return '';
  }
  const d = new Date(t);
  return `${pad2(d.getDate())}:${pad2(d.getMonth() + 1)}:${d.getFullYear()}`;
};

/**
 * Returns `start + duration` only when all inputs are valid and the resulting
 * timestamp is still in the future; otherwise `null`
 */
const futureEndMs = (start, duration, now) => {
  if (start == null || !Number.isFinite(duration) || duration <= 0) return null;
  const end = start + duration;
  return end > now ? end : null;
};

const getTypeBasedEndMs = (row, now) => {
  if (DEPOSIT_LIKE_TYPES.has(row.type)) {
    return futureEndMs(normalizeEpochMs(row.bonusAssignmentTime), row.timeToDeposit, now);
  }
  const start = normalizeEpochMs(row.createTime) ?? normalizeEpochMs(row.bonusAssignmentTime);
  return futureEndMs(start, row.timeToWager, now);
};

const usesWagerWindow = uiGroup => uiGroup === UI_GROUP.UNLOCKED || uiGroup === UI_GROUP.ACTIVATED;

/**
 * Deadline epoch ms for a bonus row:
 * 1. Future expirationTime (always preferred)
 * 2. Deposit / Reload-Deposit: bonusAssignmentTime + timeToDeposit
 * 3. Non-Deposit template: createTime (or bonusAssignmentTime) + timeToWager
 * 4. Client unlocked / activated: wagerStartTime + timeToWager
 */
export const getBonusCountdownEndMs = (row, uiGroup) => {
  const now = Date.now();

  const exp = normalizeEpochMs(row.expirationTime) ?? normalizeEpochMs(row.endTime);
  if (exp != null && exp > now) return exp;

  const typedEnd = getTypeBasedEndMs(row, now);
  if (typedEnd != null) return typedEnd;

  if (usesWagerWindow(uiGroup)) {
    return futureEndMs(normalizeEpochMs(row.wagerStartTime), row.timeToWager, now);
  }

  return null;
};

/**
 * DEPOSIT freespins: `expirationTime`
 * REGULAR freespins: keep `createTime + timeToUse`
 */
export const getFreespinCountdownEndMs = row => {
  if (row.freeSpinSubType === FreeSpinSubType.DEPOSIT) {
    return normalizeEpochMs(row.expirationTime);
  }

  const create = normalizeEpochMs(row.createTime);
  const ttu = row.freeSpin?.timeToUse;
  if (create == null || !Number.isFinite(ttu) || ttu <= 0) {
    return null;
  }
  return create + ttu;
};

/** `start + duration` (ms) with validation; `null` when inputs are invalid. */
const addMs = (startRaw, durationMs) => {
  const start = normalizeEpochMs(startRaw);
  if (start == null || !Number.isFinite(durationMs) || durationMs <= 0) return null;
  return start + durationMs;
};

/**
 * Epoch ms an EXPIRED instance went Expired (FT-8580). The BE nulls
 * `expirationTime` in client-bonus:search once a bonus is Expired, so for every
 * non-freespin type (deposit AND no-deposit) we derive it from the wager window
 * (`wagerStartTime + timeToWager`, always present), falling back to
 * expirationTime / endTime just in case:
 *   FREESPIN REGULAR  -> createTime + timeToUse
 *   FREESPIN DEPOSIT  -> expirationTime ?? createTime + timeToUse
 *   BONUS (any type)  -> wagerStartTime + timeToWager ?? expirationTime ?? endTime
 */
export const resolveExpiredEndMs = row => {
  if (row.freeSpinSubType != null) {
    const useWindowEnd = addMs(row.createTime, row.freeSpin?.timeToUse);

    return row.freeSpinSubType === FreeSpinSubType.DEPOSIT
      ? (normalizeEpochMs(row.expirationTime) ?? useWindowEnd)
      : useWindowEnd;
  }

  return (
    addMs(row.wagerStartTime, row.timeToWager) ??
    normalizeEpochMs(row.expirationTime) ??
    normalizeEpochMs(row.endTime)
  );
};
